<script setup>
import { onMounted, onBeforeUnmount, watch, reactive, ref, computed } from "vue";
import { argonTableStore } from "@/_store/argonTableStore";
import { objectUtils } from "@/utils";
import FilterSearch from "./FilterSearch.vue";
import DefaultFilter from "./DefaultFilter.vue";

var searchText = ref("");
var selectedFilters = reactive([]);
const emit = defineEmits(["filterChanged"]);

const props = defineProps({
  size: {
    type: String,
    default: "default",
  },
  icon: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: "input-search",
  },
  placeholder: {
    type: String,
    default: "",
  },
  fieldFilters: {
    type: Array,
    default() {
      return [];
    },
  },
  defaultFilters: {
    type: Array,
    default() {
      return [];
    },
  },
  showFilters: {
    type: Boolean,
    default: true,
  },
  showSearch: {
    type: Boolean,
    default: true,
  },
});

const suggestionRefs = ref([]);
const index = ref(0);
const tableStore = argonTableStore();
const dropdownOpen = ref(false);
const disableButton = ref(false);
const filterColumns = ref([]);
onMounted(() => {
  selectedFilters = reactive(objectUtils.copyByValue(tableStore?.filters));
  document.addEventListener("click", handleClickOutside);
  splitDefaultFiltersIntoColumns(props.defaultFilters);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

watch(
  () => tableStore.filters,
  (value) => {
    const newFilters =objectUtils.copyByValue(value);
    selectedFilters.splice(0,selectedFilters.length, ...newFilters);
  }
);

watch(
  () => props.defaultFilters,
  (value) => {
    splitDefaultFiltersIntoColumns(value);
  },
  { deep: true }
);


watch(
  () => tableStore.filtersCustom,
  () => {
    disableButton.value = false
  }
);

watch(searchText, (newVal) => {
  if (searchText.value != "") dropdownOpen.value = true;
  if (newVal && suggestionRefs.value.length > 0) {
    index.value = 0;
    suggestionRefs.value[index.value].classList.add("focused");
  }
});

watch(dropdownOpen, () => {});

const activeDefaultFilterCount = computed(() =>
  selectedFilters.filter((f) => f.type === "default").length
);

function clearDefaultFilters() {
  const remaining = selectedFilters.filter((f) => f.type !== "default");
  selectedFilters.splice(0, selectedFilters.length, ...remaining);
  emit("filterChanged", selectedFilters);
}

function splitDefaultFiltersIntoColumns(array) {
  filterColumns.value = (array || []).map((filter) => [filter]);
}
function searchOnText(filter) {
  selectedFilters.push({
    field: filter.field,
    description: filter.description,
    text: searchText.value,
    type: "text",
    exact: filter.exact,
  });
  document.getElementById(props.id).focus();
  searchText.value = "";
  dropdownOpen.value = false;
  emit("filterChanged", selectedFilters);
}

function cleanFilters(filters) {
  const groupedData = filters.reduce(
    (acc, item) => {
      const key = `${item.field}-${item.description}`;
      if (item.type === "default") {
        acc.defaults.push(item);
      } else {
        if (!acc.grouped[key]) {
          acc.grouped[key] = {
            field: item.field,
            description: item.description,
            text: [],
            value: { $in: [] },
            type: "array",
          };
        }
        if (item.type === "text") {
          acc.grouped[key].text.push(item.text);
          acc.grouped[key].value.$in.push(
            item.text.toUpperCase().replace(/ /g, "_")
          );
        } else {
          acc.grouped[key].text.push(...item.text);
          acc.grouped[key].value.$in.push(
            ...item.text.map((text) => text.toUpperCase().replace(/ /g, "_"))
          );
        }
      }
      return acc;
    },
    { defaults: [], grouped: {} }
  );

  const result = Object.values(groupedData.grouped);

  return [...groupedData.defaults, ...result];
}

function removeFilter(filter) {
  var index = selectedFilters.findIndex(
    (element) => element.field == filter.field && element.text == filter.text
  );
  selectedFilters.splice(index, 1);
  emit("filterChanged", selectedFilters);
}

function updateMultiSelectFilter(filter, index) {
  if (filter.active) {
    selectedFilters[index].value.$in.push(filter.value);
    selectedFilters[index].text.push(filter.label);
  } else {
    removeValueFromMultiSelect(index, filter.value);
  }
}

function updateSingleSelectFilter(filter, index) {
  if (filter.active) {
    selectedFilters[index].value = filter.value;
    selectedFilters[index].text = filter.label;
  } else {
    selectedFilters.splice(index, 1);
  }
}

function removeValueFromMultiSelect(index, value) {
  const valueIndex = selectedFilters[index].value.$in.findIndex(
    (element) => element === value
  );
  selectedFilters[index].value.$in.splice(valueIndex, 1);
  selectedFilters[index].text.splice(valueIndex, 1);
  if (selectedFilters[index].text.length === 0) {
    selectedFilters.splice(index, 1);
  }
}

function updateFilterValue(filter, index) {
  if (filter.multi) {
    updateMultiSelectFilter(filter, index);
  } else {
    updateSingleSelectFilter(filter, index);
  }
}

function addFilter(filter) {
  let value;
  let label;
  if (filter.multi) {
    value = { $in: [filter.value] };
    label = [filter.label];
  } else {
    value = filter.value;
    label = filter.label;
  }
  let newFilter={
    field: filter.field,
    description: filter.description,
    text: label,
    value: value,
    type: "default",
    isDate: filter.isDate
  }

  selectedFilters.push(newFilter);
}

function change(filter) {
  const index = selectedFilters.findIndex(
    (element) => element.field === filter.field
  );
  if (index >= 0) {
    updateFilterValue(filter, index);
  } else {
    addFilter(filter);
  }
  emit("filterChanged", selectedFilters);
}

function handleKeydown(event) {
  const keyActions = {
    ArrowDown: () => {
      if (index.value < suggestionRefs.value.length - 1) index.value++;
    },
    ArrowUp: () => {
      if (index.value > 0) index.value--;
    },
    Enter: () => {
      if (index.value >= 0) suggestionRefs.value[index.value]?.click();
    },
    Backspace: () => {
      if (
        !searchText.value ||
        (searchText.value.length === 0 && selectedFilters.length > 0)
      ) {
        removeFilter(selectedFilters[selectedFilters.length - 1]);
      }
    },
  };

  const action = keyActions[event.key];
  if (action) {
    if (event.key !== "Backspace") {
      event.preventDefault();
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      suggestionRefs.value[index.value]?.classList.remove("focused");
    }
    action();
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      suggestionRefs.value[index.value]?.classList.add("focused");
    }
  }
}

const handleClickOutside = (event) => {
  if (!event.target.closest(".dropdown")) {
      dropdownOpen.value = false;
      searchText.value = "";
  }
};

</script>
<template>
  <div
    v-if="showSearch || showFilters"
    class="searcher row d-flex border align-items-center justify-content-between ps-2"
  >
    <div v-if="showSearch" class="col-md-auto p-0">
      <i class="fa fa-search"></i>
    </div>
    <div
      v-if="showSearch"
      class="col row ms-0 p-0 border-0 dropdown me-2"
      :aria-expanded="dropdownOpen"
    >
      <filter-search
        v-for="filter in cleanFilters(selectedFilters)"
        :key="filter.field + filter.text"
        :field="filter.field"
        :text="filter.text"
        :description="filter.description"
        @remove-filter="removeFilter"
      >
      </filter-search>
      <input
        :id="id"
        v-model="searchText"
        :name="name"
        :placeholder="placeholder"
        type="input"
        class="col"
        @keydown="handleKeydown"
        @focus="dropdownOpen = true"
      />
      <div
        v-if="dropdownOpen && searchText"
        class="searcher-menu z-index-1030 border rounded mt-2 p-1"
        aria-labelledby="searcherTextDropdown"
        style
      >
        <a
          v-for="filter in fieldFilters"
          ref="suggestionRefs"
          :key="filter.field"
          class="dropdown-item border-radius-md"
          @click="searchOnText(filter)"
          >Buscar <b>{{ filter.description }}</b> por {{ searchText }}</a
        >
        <a
          v-if="fieldFilters.length == 0"
          class="dropdown-item border-radius-md label-placeholder text-sm text-center"
          >
          Búsqueda no disponible</a>
      </div>
    </div>
    <div v-if="showFilters" class="col-md-auto p-0 dropdown">
      <a
        id="searcherFilterDropdown"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        data-bs-auto-close="outside"
        class="dropdown-toggle me-2"
      >
        <span v-if="activeDefaultFilterCount > 0" class="filter-count-badge">
          {{ activeDefaultFilterCount }}
        </span>
      </a>
      <div
        class="dropdown-menu z-index-1030 dropdown-menu-end mt-1 filters"
        aria-labelledby="searcherFilterDropdown"
      >
        <div class="filter-panel-header d-flex align-items-center justify-content-between px-3" style="padding-top: 0.4rem; padding-bottom: 0.3rem;">
          <span class="filter-panel-title">
            <i class="fa fa-filter me-1"></i> Filtros
          </span>
          <a
            v-if="activeDefaultFilterCount > 0"
            class="filter-clear-link text-sm"
            @click.stop="clearDefaultFilters"
          >
            Limpiar todo
          </a>
        </div>
        <hr class="mt-0 mb-0 filter-divider" />
        <div
          class="row"
          :style="filterColumns.length ? { width: `${filterColumns.length * 165}px` } : null"
        >
          <div
            v-for="(column, columnIndex) in filterColumns"
            :key="columnIndex"
            class="col divider pb-2"
          >
            <default-filter
              v-for="(filter, i) in column"
              :key="i"
              :filter="filter"
              :divider="i != column.length - 1"
              @change="change"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.z-index-1030 {
  z-index: 1030 !important;
}

.divider {
  border-right: 1px solid rgba(0, 0, 0, 0.06);
}

.filter-divider {
  height: 0;
  border: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background-color: transparent;
  opacity: 1;
}

.filters {
  max-height: 75vh;
  overflow-y: auto;
  overflow-x: hidden;
  min-width: 185px !important;
  border-radius: 0.75rem !important;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.12) !important;
}
.focused {
  color: #344767;
  background-color: #e9ecef;
}

.filter-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.1rem;
  height: 1.1rem;
  padding: 0 0.3rem;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1;
  border-radius: 999px;
  background-color: #6c757d;
  color: #fff;
}

/* Filter panel header */
.filter-panel-header {
  min-height: 2.5rem;
}

.filter-panel-title {
  font-size: 0.90rem;
  font-weight: 600;
  color: #344767;
}

.filter-clear-link {
  font-size: 0.75rem;
  color: #8392ab;
  cursor: pointer;
  &:hover {
    color: #e53e3e;
    text-decoration: underline;
  }
}
</style>
